import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { map, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environment';

import { LANGUAGES, Language } from '@core/constants/language.constants';
import { CompanySettingService } from '@services/company-setting.service';
import { CompanySetting } from '@models/company-setting.model';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private supportedLangs: Language[] = Object.values(LANGUAGES).filter(
    (lang): lang is Language => lang !== LANGUAGES.ZZ,
  );

  private readonly _currentLanguage = signal<Language>(LANGUAGES.EN);
  readonly currentLanguage = this._currentLanguage.asReadonly();

  private readonly companySettingService = inject(CompanySettingService);

  // The current company's active content languages, tracked reactively from the company stream.
  private readonly _companyActiveLanguages = toSignal(
    this.companySettingService.companySettingListener.pipe(
      map(
        (company: CompanySetting | null) => company?.enabledLanguages ?? null,
      ),
    ),
    { initialValue: null },
  );

  // THE single frontend read seam for "which languages this company may use". Every language
  // selector reads this. Falls back to all platform-supported languages when there is no company
  // context (guest pages) or the company has no explicit set — so nothing ever renders empty.
  readonly activeLanguages = computed<Language[]>(() => {
    const enabled = this._companyActiveLanguages();
    if (!enabled?.length) {
      return this.getSupportedLanguages();
    }
    const active = this.supportedLangs.filter(lang => enabled.includes(lang));
    return active.length ? active : this.getSupportedLanguages();
  });

  constructor(
    private translateService: TranslateService,
    private appTitle: Title,
  ) {
    if (environment.development) {
      this.supportedLangs.push(LANGUAGES.ZZ);
    }
  }

  setMemberLanguage(memberLang: string, companyDefaultLang: string): void {
    if (this.isSupported(memberLang)) {
      this._currentLanguage.set(memberLang);
    } else {
      const browserLang = this.translateService.getBrowserLang();
      if (browserLang && this.isSupported(browserLang)) {
        this._currentLanguage.set(browserLang);
      } else if (companyDefaultLang && this.isSupported(companyDefaultLang)) {
        this._currentLanguage.set(companyDefaultLang);
      }
    }

    this.setLanguage();
  }

  setLanguage(): void {
    this.translateService.addLangs(this.supportedLangs);
    this.translateService.setDefaultLang(
      environment.development ? LANGUAGES.ZZ : LANGUAGES.EN,
    );

    this.translateService
      .use(this._currentLanguage())
      .pipe(take(1))
      .subscribe(() =>
        this.appTitle.setTitle(this.translateService.instant('app.title')),
      );

    window.WEBSPELLCHECKER_CONFIG.localization = this._currentLanguage();
  }

  setLanguageFromCookieOrBrowser(cookieLang: string): void {
    if (this.isSupported(cookieLang)) {
      this._currentLanguage.set(cookieLang);
    } else {
      const browserLang = this.translateService.getBrowserLang();
      if (browserLang && this.isSupported(browserLang)) {
        this._currentLanguage.set(browserLang);
      }
    }
    this.setLanguage();
  }

  getSupportedLanguages(): Language[] {
    return this.supportedLangs;
  }

  // Active languages for a selector that edits an existing record: the company's active set plus the
  // record's own (possibly deactivated) language, so an already-authored value still renders instead
  // of showing blank. Deactivation is a visibility filter, never data loss.
  activeLanguagesIncluding(code: string | undefined | null): Language[] {
    const active = this.activeLanguages();
    if (code && this.isSupported(code) && !active.includes(code)) {
      return [...active, code];
    }
    return active;
  }

  getDefaultLanguage(): Language {
    return LANGUAGES.EN;
  }

  private isSupported(lang: string): lang is Language {
    return (this.supportedLangs as string[]).includes(lang);
  }
}
